import { condition, recommendations } from "@api/index";
import { RecommendedConditions } from "@types-runway/case";
import { ConditionTypes, Medication } from "@types-runway/condition";
import { Gender } from "@types-runway/user";
import { mapConditionPrescriptions } from "@utils/case/conditionPrescriptions";
import { RECOMMENDATION_TIMEOUT_MS } from "@utils/constantsEnv";
import { hasTimeForLabExam, medicationRequiresLabExam } from "@utils/labExam/labExam";

const useAddons = () => {
  const getAddons = async (
    userGender: Gender,
    preselectedConditions: ConditionTypes[],
    tripDescription?: string,
    departureDate?: string
  ): Promise<RecommendedConditions> => {
    try {
      const response = await recommendations.recommendationPost(
        "/addon",
        {
          userGender,
          preselectedConditions,
          tripDescription,
        },
        { timeout: RECOMMENDATION_TIMEOUT_MS }
      );
      const mappedConditions = await mapConditionPrescriptions(response.data.conditions, 0);

      let conditionsWithLeadTime = mappedConditions;
      if (departureDate && !hasTimeForLabExam(departureDate)) {
        const medicationsByCondition = await Promise.all(
          mappedConditions.map(async (cond) => {
            try {
              const encodedName = encodeURIComponent(cond.product.name);
              const { data } = await condition.conditionGet(`/${encodedName}/medications`, {
                timeout: RECOMMENDATION_TIMEOUT_MS,
              });
              return { conditionName: cond.product.name, medications: data.medications as Medication[] };
            } catch {
              return null;
            }
          })
        );

        const resolvedMedications = medicationsByCondition.filter(
          (entry): entry is NonNullable<typeof entry> => entry !== null
        );

        conditionsWithLeadTime = mappedConditions.filter((cond) => {
          const entry = resolvedMedications.find((m) => m.conditionName === cond.product.name);
          if (!entry) return true;
          return entry.medications.some((med) => !medicationRequiresLabExam(med));
        });
      }

      return {
        ...response.data,
        conditions: conditionsWithLeadTime,
      };
    } catch (error) {
      console.error(error);
      return {
        conditions: [],
      };
    }
  };

  return {
    getAddons,
  };
};

export default useAddons;
